import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActionCreator, Creator } from '@ngrx/store';
import { of } from 'rxjs';
import { exhaustMap, take } from 'rxjs/operators';
import { LoadingSpinnerService } from '@arc/unified-payments-frontend/shared-payment';

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that shows a loading
 * spinner from a trigger action until one of a set of complete actions fires — the functional
 * equivalent of BaseEffect.loadUntilActionComplete$, for effects built as standalone functions
 * rather than classes.
 *
 * Usage (one instance per API call in an effect chain):
 *
 *   export const poiReceiptLandingSessionLoadingEffect = createLoadUntilActionsCompleteEffect({
 *     triggerActions: [PoiPaymentReceiptComponentActions.initialized],
 *     completeActions: [
 *       PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *       PoiPaymentReceiptComponentActions.loadSessionSummaryError,
 *     ],
 *   });
 */
export function createLoadUntilActionsCompleteEffect(config: {
  triggerActions: ActionCreator<string, Creator>[];
  completeActions: ActionCreator<string, Creator>[];
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(...config.triggerActions),
        exhaustMap(() => {
          loadingSpinnerService.loadUntil(
            actions$.pipe(ofType(...config.completeActions), take(1)),
          );
          return of();
        }),
      ),
    { functional: true, dispatch: false },
  );
}
